import React from 'react';
import type { User } from '../types';

interface HeaderProps {
  user: User;
  onLoginClick: () => void;
  onLogout: () => void;
  onViewLibrary: () => void;
  onHomeClick: () => void;
  showLibrary: boolean;
  isDemoMode?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ user, onLoginClick, onLogout, onViewLibrary, onHomeClick, showLibrary, isDemoMode }) => {
  return (
    <>
      {isDemoMode && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-2 px-4 text-sm font-bold">
          🎭 DEMO MODE — Running without database connection. UI preview only.
        </div>
      )}
      <header className="w-full bg-white py-4 shadow-sm border-b-4 border-[#3db8c5] relative overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none select-none overflow-hidden">
          <div className="text-[12rem] font-black absolute -bottom-10 -right-20 transform rotate-12">SCP</div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 relative z-10">

          {/* Brand Block - Logo on Left - Clickable to go home */}
          <button 
            onClick={onHomeClick}
            className="flex items-center gap-4 lg:gap-6 w-full md:w-auto hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="flex-shrink-0">
              <img
                src="https://dl.dropboxusercontent.com/scl/fi/76ivk8y2o4k3fnt2iqu9y/Logo.png?rlkey=ve3uy6t4rsjw1rxe5kkir065z&raw=1"
                alt="SCP Agency"
                className="h-10 md:h-12 w-auto object-contain"
                draggable={false}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>

            <div className="hidden md:block h-8 w-[2px] bg-[#002d5b]/10"></div>

            <div className="flex flex-col text-left">
              <h1 className="font-['Rubik'] font-black text-2xl md:text-3xl italic uppercase tracking-tighter leading-none flex flex-wrap">
                <span className="text-[#002d5b]">Scriptor</span>
              </h1>
              <span className="text-[10px] font-black text-[#3db8c5] uppercase tracking-[0.3em] mt-1 hidden md:block">
                Strategic Creative Intelligence
              </span>
            </div>
          </button>

          {/* Auth & Navigation */}
          <div className="flex items-center space-x-3">
            {user.isLoggedIn ? (
              <>
                <button
                  onClick={onViewLibrary}
                  className={`px-4 py-2 rounded font-black uppercase text-[10px] tracking-widest transition-all ${showLibrary ? 'bg-[#f15a24] text-white' : 'bg-[#002d5b] text-[#3db8c5] hover:bg-[#003d7b]'
                    }`}
                >
                  {showLibrary ? 'Back to Generator' : 'My Library'}
                </button>
                <a
                  href="https://scpagency.ai/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded font-black uppercase text-[10px] tracking-widest transition-all bg-[#f15a24] text-white hover:bg-[#d14a14]"
                >
                  Visit Store
                </a>
                <div className="flex items-center bg-black/5 px-3 py-2 rounded border border-[#002d5b]/10">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                  <span className="text-[10px] font-black text-[#002d5b] uppercase">{user.username}</span>
                  <button onClick={onLogout} className="ml-3 text-[10px] text-[#f15a24] font-black hover:underline uppercase">Exit</button>
                </div>
              </>
            ) : (
              <button
                onClick={onLoginClick}
                className="bg-[#002d5b] text-white px-5 py-2 rounded font-black uppercase text-[10px] tracking-widest hover:bg-[#003d7b] transition-all border-b-2 border-[#f15a24] btn-hover-scale"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>
    </>
  );
};